'use client';

import { useEffect, useState } from 'react';
import Image from 'next/image';
import { usePathname } from 'next/navigation';
import SocialMediaShare from '@/components/shared/socialMediaShare';
import { getDotcmsImageURL } from '@/app/utils/getDotcmsImageURL';

export function NewsDetailHeader({ title, publishDate, tags = [], titleImage, thumbnailAlt }) {
    const pathname = usePathname();
    const [shareUrl, setShareUrl] = useState('');

    useEffect(() => {
        setShareUrl(new URL(pathname, window.location.origin).href);
    }, [pathname]);

    const imageSrc = titleImage?.idPath || titleImage?.versionPath;
    const imageUrl = imageSrc ? getDotcmsImageURL({ src: imageSrc, width: 800 }) : null;

    return (
        <div className="background-crossline p-4 pt-8 lg:p-12 lg:px-24">
            <div className="container flex flex-col gap-6 p-0">
                {/* Tags */}
                {tags.length > 0 && (
                    <div className="flex flex-wrap gap-2">
                        {tags.map((tag) => (
                            <span
                                key={tag.text}
                                className={`rounded-full px-3 py-1 text-xs font-bold uppercase tracking-widest ${tag.bgColor} ${tag.fontColor}`}>
                                {tag.text}
                            </span>
                        ))}
                    </div>
                )}

                {/* Title + image side-by-side */}
                <div className="flex flex-col gap-2 lg:flex-row lg:items-start lg:gap-5">
                    {/* Left: title and meta */}
                    <div className="flex flex-1 flex-col gap-5">
                        <h1 className="text-balance text-3xl font-bold leading-tight md:text-4xl lg:text-5xl">
                            {title}
                        </h1>

                        <div className="flex flex-wrap items-center gap-4 border-t border-blue-200/60 pt-4">
                            {publishDate && (
                                <time className="text-sm text-blue-600">{publishDate}</time>
                            )}
                            {publishDate && shareUrl && (
                                <span className="text-blue-300" aria-hidden="true">
                                    ·
                                </span>
                            )}
                            {shareUrl && (
                                <div className="flex items-center gap-3">
                                    <span className="text-sm text-blue-600">Share on:</span>
                                    <SocialMediaShare url={shareUrl} />
                                </div>
                            )}
                        </div>
                    </div>

                    {/* Right: image */}
                    <div className="relative w-full overflow-hidden rounded-xl lg:w-[34%] lg:shrink-0">
                        <div className="relative aspect-video w-full">
                            {imageUrl ? (
                                <Image
                                    src={imageUrl}
                                    alt={thumbnailAlt || title}
                                    fill
                                    priority
                                    className="object-cover"
                                    sizes="(max-width: 1024px) 100vw, 40vw"
                                />
                            ) : (
                                <Image
                                    src="/assets/newsroom/newsroom-placeholder.png"
                                    alt={thumbnailAlt || title}
                                    fill
                                    priority
                                    className="object-cover"
                                    sizes="(max-width: 1024px) 100vw, 40vw"
                                />
                            )}
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
}
